import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Contact } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import { ContactFormModal } from './ContactFormModal.js';
import { PortalFlagBadges, notificationCategoryNames } from './ContactsSection.js';
import { MailerliteStatusBadge } from './MailerliteStatusBadge.js';
import { useAllContacts } from './api.js';

const COLUMN_COUNT = 9;

export function ContactsPage(): JSX.Element {
  const { can } = useAuth();
  const canEdit = can('customers', 'edit');
  const canDelete = can('customers', 'delete');

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [status, setStatus] = useState('active');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);

  // Debounce the search box so each keystroke doesn't fire a request.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const query = useAllContacts({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    search: search || undefined,
    customerId: customerId || undefined,
    isActive: status === '' ? undefined : status === 'active',
  });

  const contacts = query.data?.data ?? [];
  const pageInfo = query.data?.page;
  const hasFilters = Boolean(search) || Boolean(customerId) || status !== 'active';

  const serviceCategoryOptions = useServiceCategoryOptions().data ?? [];
  const categoryNameById = useMemo(
    () => new Map(serviceCategoryOptions.map((c) => [c.id, c.name])),
    [serviceCategoryOptions],
  );

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (contact: Contact): void => {
    setEditing(contact);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Contacts"
        description="Every customer contact in the PSA."
        actions={
          canEdit ? (
            <Button onClick={openCreate}>
              <Icon name="customers" className="h-4 w-4" />
              New contact
            </Button>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-72">
          <CustomerSelectField
            label="Customer"
            value={customerId}
            onChange={(id) => {
              setCustomerId(id);
              setPage(1);
            }}
            allowClear
            placeholder="All customers"
          />
        </div>
        <div className="w-72">
          <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="contact-search">
            Search
          </label>
          <input
            id="contact-search"
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name, email, or title…"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div className="w-40">
          <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="contact-status">
            Status
          </label>
          <select
            id="contact-status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="">All</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[1000px] text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Title</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Notifications for</th>
              <th className="px-4 py-3 font-medium">Billing</th>
              <th className="px-4 py-3 font-medium">Cat+</th>
              <th className="px-4 py-3 font-medium">MailerLite</th>
              <th className="px-4 py-3 font-medium">Catalyst Portal</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              isEmpty={contacts.length === 0}
              emptyTitle={hasFilters ? 'No contacts match your filters.' : 'No contacts yet.'}
              emptyDescription={
                hasFilters ? 'Try clearing the search or customer filter.' : undefined
              }
              onRetry={() => void query.refetch()}
              colSpan={COLUMN_COUNT}
            >
              {contacts.map((contact) => (
                <tr
                  key={contact.id}
                  className="cursor-pointer align-top hover:bg-slate-50"
                  onClick={() => openEdit(contact)}
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium text-slate-900">
                        {`${contact.firstName} ${contact.lastName ?? ''}`.trim()}
                      </span>
                      {contact.isPrimary ? <Badge tone="blue">Primary</Badge> : null}
                      {!contact.isActive ? <Badge tone="gray">Inactive</Badge> : null}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/customers?returnCustomerId=${contact.customerId}&returnTab=contacts`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-brand-700 hover:underline"
                    >
                      {contact.customerName}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{contact.title || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{contact.email || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {notificationCategoryNames(contact, categoryNameById)}
                  </td>
                  <td className="px-4 py-3">
                    {contact.billingContact ? <Badge tone="blue">Billing</Badge> : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {contact.catPlusNotifications ? <Badge tone="green">Cat+</Badge> : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <MailerliteStatusBadge contact={contact} />
                  </td>
                  <td className="px-4 py-3">
                    <PortalFlagBadges contact={contact} />
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
        {pageInfo ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <ContactFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        contact={editing}
        canEdit={canEdit}
        canDelete={canDelete}
      />
    </div>
  );
}
